import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { Voucher } from '../voucher.model';
import { ExpenseService, ExpensePaymentVoucherRequest } from '../../../../expense/expense.service';
import { BankAccount } from '../../../bank-account/bank-account.model';
import { ChartOfAccounts } from '../../../../chart-of-accounts/chart-of-accounts.model';
import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';

@Component({
    selector: 'app-expense-payment-draft-editor', standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, InputNumberModule, SelectModule, ToastModule,
        PageShellComponent, PageHeroComponent, PageFormSectionComponent],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>
            <app-page-hero eyebrow="Payables" title="Edit Expense Payment Voucher Draft"
                [subtitle]="voucher.voucherNumber || 'Expense payment'">
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                    <p-button label="Save Draft" icon="pi pi-save" [loading]="saving" [disabled]="!valid" (click)="save()"></p-button>
                </div>
            </app-page-hero>
            <app-page-form-section title="Expense Payment" icon="pi pi-wallet" subtitle="Update the draft amount and payment account for the linked expense.">
                <p>Expense: {{ voucher.sourceReference }} · Payee: {{ voucher.crmDisplayName || voucher.mainParty?.entityName }}</p>
                <div class="grid gap-4" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr))">
                    <div><label for="expensePaymentAmount">Amount *</label><p-inputNumber inputId="expensePaymentAmount" [(ngModel)]="amount" [min]="0" [minFractionDigits]="4" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber></div>
                    <div><label for="expensePaymentMode">Payment Mode *</label><p-select inputId="expensePaymentMode" [(ngModel)]="mode" [options]="modes" optionLabel="label" optionValue="value" styleClass="w-full"></p-select></div>
                    <div *ngIf="mode === 'CASH'"><label for="expenseCashAccount">Cash Account *</label><p-select inputId="expenseCashAccount" [(ngModel)]="cashAccountId" [options]="cashAccounts" optionLabel="accountName" optionValue="accountId" [filter]="true" styleClass="w-full"></p-select></div>
                    <div *ngIf="mode !== 'CASH'"><label for="expenseBankAccount">Company Bank Account *</label><p-select inputId="expenseBankAccount" [(ngModel)]="bankAccountId" [options]="currencyBanks" optionLabel="accountName" optionValue="id" [filter]="true" styleClass="w-full"></p-select></div>
                </div>
            </app-page-form-section>
        </app-page-shell>`
})
export class ExpensePaymentDraftEditorComponent implements OnChanges {
    @Input({ required: true }) voucher!: Voucher;
    @Input() canSave = false;
    @Input() cashAccounts: ChartOfAccounts[] = [];
    @Input() banks: BankAccount[] = [];
    amount = 0;
    mode: ExpensePaymentVoucherRequest['paymentMode'] = 'CASH';
    cashAccountId?: number;
    bankAccountId?: number;
    saving = false;
    readonly modes = [{ label: 'Cash', value: 'CASH' }, { label: 'Bank Transfer', value: 'BANK_TRANSFER' }, { label: 'Cheque', value: 'CHEQUE' }];

    constructor(private expenses: ExpenseService, private router: Router, private messages: MessageService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['voucher']) return;
        this.amount = this.voucher.amount || 0;
        this.mode = this.voucher.paymentMode || 'CASH';
        this.cashAccountId = this.voucher.cashAccountId;
        this.bankAccountId = this.voucher.fromBankAccount?.id;
    }

    get currencyBanks(): BankAccount[] {
        return this.banks.filter(bank => bank.currencyCode?.toUpperCase() === this.voucher.country?.currencyCode?.toUpperCase());
    }

    get valid(): boolean {
        return this.canSave && !this.saving && !!this.voucher.expenseId && isEditableFinancialDraft(this.voucher)
            && Number.isFinite(this.amount) && this.amount > 0 && !!(this.mode === 'CASH' ? this.cashAccountId : this.bankAccountId);
    }

    save(): void {
        if (!this.valid || !this.voucher.voucherId || !this.voucher.expenseId) return;
        this.saving = true;
        this.expenses.updatePaymentVoucher(this.voucher.expenseId, this.voucher.voucherId, {
            amount: this.amount, paymentMode: this.mode,
            cashAccountId: this.mode === 'CASH' ? this.cashAccountId : null,
            fromBankAccountId: this.mode === 'CASH' ? null : this.bankAccountId
        }).pipe(finalize(() => this.saving = false)).subscribe({
            next: () => this.back(),
            error: error => this.messages.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not update the draft expense payment.' })
        });
    }

    back(): void { this.router.navigate(['/accounts/enterprise/payables/generated-vouchers/view', this.voucher.voucherId]); }
}
